import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { BankService } from '@core/services/bank.service';
import { ApiResponse } from '@core/types';
import { Bank, BankCreate, BankUpdate } from '@core/types/finance.type';
import { QUERY_KEYS } from '@core/constans';
import { lastValueFrom } from 'rxjs';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class BankQuery {
  private readonly bankService = inject(BankService);
  private readonly queryClient = inject(QueryClient);

  useBanks() {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.banks.list(),
      queryFn: () => lastValueFrom(this.bankService.getBanks()),
      select: (data: ApiResponse<Bank[]>) => data.data || [],
      staleTime: environment.staleTime,
    }));
  }

  useCreateBank() {
    return injectMutation(() => ({
      mutationFn: (data: BankCreate) =>
        lastValueFrom(this.bankService.createBank(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.banks.all });
      },
    }));
  }

  useUpdateBank() {
    return injectMutation(() => ({
      mutationFn: (data: BankUpdate) =>
        lastValueFrom(this.bankService.updateBank(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.banks.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
      },
    }));
  }

  useDeleteBank() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.bankService.deleteBank(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.banks.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
      },
    }));
  }
}
